<template>
  <div class="element-shadow">
    <div class="row enable-row">
      <div class="enable-label">Enable shadow:</div>
      <div class="switch-wrapper">
        <Switch :value="hasShadow" @update:value="value => toggleShadow(value)" />
      </div>
    </div>
    <template v-if="hasShadow && shadow">
      <div class="row">
        <div class="row-label">Horizontal offset:</div>
        <Slider 
          style="width: 56%;"
          :min="-10" 
          :max="10" 
          :step="1" 
          :value="shadow.h" 
          @update:value="value => updateShadow({ h: value as number })"
        />
      </div>
      <div class="row">
        <div class="row-label">Vertical offset:</div>
        <Slider
          style="width: 56%;"
          :min="-10"
          :max="10"
          :step="1"
          :value="shadow.v"
          @update:value="value => updateShadow({ v: value as number })"
        />
      </div>
      <div class="row">
        <div class="row-label">Blur radius:</div>
        <Slider
          style="width: 56%;"
          :min="1"
          :max="20"
          :step="1"
          :value="shadow.blur"
          @update:value="value => updateShadow({ blur: value as number })"
        />
      </div>
      <div class="row">
        <div class="row-label">Shadow color:</div>
        <Popover trigger="click" style="width: 56%;">
          <template #content>
            <ColorPicker
              :modelValue="shadow.color"
              @update:modelValue="value => updateShadow({ color: value })"
            />
          </template>
          <ColorButton :color="shadow.color" />
        </Popover>
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useMainStore, useSlidesStore } from '@/store'
import type { PPTElementShadow } from '@/types/slides'
import useHistorySnapshot from '@/hooks/useHistorySnapshot'

import ColorButton from '@/components/ColorButton.vue'
import ColorPicker from '@/components/ColorPicker/index.vue'
import Switch from '@/components/Switch.vue'
import Slider from '@/components/Slider.vue'
import Popover from '@/components/Popover.vue'

const slidesStore = useSlidesStore()
const { theme } = storeToRefs(slidesStore)
const { handleElement } = storeToRefs(useMainStore())

const shadow = ref<PPTElementShadow>()
const hasShadow = ref(false)

watch(handleElement, () => {
  if (!handleElement.value) return
  shadow.value = 'shadow' in handleElement.value ? handleElement.value.shadow : undefined
  hasShadow.value = !!shadow.value
}, { deep: true, immediate: true })

const { addHistorySnapshot } = useHistorySnapshot()

const updateShadow = (shadowProps: Partial<PPTElementShadow>) => {
  if (!handleElement.value || !shadow.value) return
  const _shadow = { ...shadow.value, ...shadowProps }
  slidesStore.updateElement({ id: handleElement.value.id, props: { shadow: _shadow } })
  addHistorySnapshot()
}

const toggleShadow = (checked: boolean) => {
  if (!handleElement.value) return
  if (checked) {
    const _shadow: PPTElementShadow = theme.value.shadow
    slidesStore.updateElement({ id: handleElement.value.id, props: { shadow: _shadow } })
  }
  else {
    slidesStore.removeElementProps({ id: handleElement.value.id, propName: 'shadow' })
  }
  addHistorySnapshot()
}
</script>

<style lang="scss" scoped>
.row {
  width: 100%;
  min-height: 30px;
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}
.row-label {
  width: 44%;
  line-height: 1.05;
  white-space: normal;
  word-break: break-word;
  padding-right: 8px;
}
.enable-row {
  justify-content: flex-start;
}
.enable-label {
  width: auto;
}
.switch-wrapper {
  width: auto;
  margin-left: 8px;
  text-align: left;
}
</style>
